import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Star, Check, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export default function TestimonialsPage() {
  const { user } = useAuth();
  const isClient = user?.role === 'CLIENT';
  const [testimonials, setTestimonials] = useState([]);
  const [publicTestimonials, setPublicTestimonials] = useState([]);
  const [newContent, setNewContent] = useState('');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const [tRes, pRes] = await Promise.all([api.get('/testimonials'), api.get('/testimonials/public')]);
      setTestimonials(tRes.data);
      setPublicTestimonials(pRes.data);
    };
    fetch().catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post('/testimonials', { content: newContent });
    setNewContent('');
    setShowForm(false);
    const tRes = await api.get('/testimonials');
    setTestimonials(tRes.data);
  };

  const handleApprove = async (id, visibility) => {
    await api.put(`/testimonials/${id}/approve?visibility=${visibility}`);
    const tRes = await api.get('/testimonials');
    setTestimonials(tRes.data);
  };

  const visColor = { PENDING: 'bg-[#F5B041]/10 text-[#F5B041]', PUBLIC: 'bg-[#2E8B57]/10 text-[#2E8B57]', PRIVATE: 'bg-[#4A6B68]/10 text-[#4A6B68]' };

  return (
    <div className="space-y-6" data-testid="testimonials-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Testimonials</h1>
        {isClient && <Button onClick={() => setShowForm(!showForm)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="write-testimonial-btn"><Star className="w-4 h-4 mr-2" />Write Testimonial</Button>}
      </div>

      {showForm && (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl">
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-3">
              <Textarea value={newContent} onChange={e => setNewContent(e.target.value)} required rows={3} placeholder="Share your experience with Mind Care India..." className="rounded-xl border-[#E5F2F1]" data-testid="testimonial-input" />
              <p className="text-xs text-[#4A6B68]">Once submitted, your testimonial cannot be edited.</p>
              <Button type="submit" className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="submit-testimonial-btn">Submit</Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* My/All Testimonials */}
      <div>
        <h3 className="text-lg font-semibold text-[#1A3330] font-['Outfit'] mb-3">{isClient ? 'My Testimonials' : 'All Testimonials'}</h3>
        <div className="space-y-3">
          {testimonials.map(t => (
            <Card key={t._id} className="bg-white border-[#E5F2F1] rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    {t.client_name && <p className="text-sm font-medium text-[#1A3330]">{t.client_name}</p>}
                    <p className="text-xs text-[#4A6B68]">{new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${visColor[t.visibility] || ''} text-[10px] border-0`}>{t.visibility}</Badge>
                    {!isClient && t.visibility === 'PENDING' && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleApprove(t._id, 'PUBLIC')} className="text-[#2E8B57] h-7" data-testid={`approve-${t._id}`}><Check className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleApprove(t._id, 'PRIVATE')} className="text-[#D3544E] h-7" data-testid={`reject-${t._id}`}><X className="w-3 h-3" /></Button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-sm text-[#1A3330]">{t.content}</p>
              </CardContent>
            </Card>
          ))}
          {testimonials.length === 0 && <p className="text-sm text-[#4A6B68]">No testimonials yet.</p>}
        </div>
      </div>

      {/* Public Testimonials Wall */}
      {publicTestimonials.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-[#1A3330] font-['Outfit'] mb-3">Public Testimonials</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {publicTestimonials.map(t => (
              <Card key={t._id} className="bg-[#F4FAF9] border-[#E5F2F1] rounded-2xl">
                <CardContent className="p-4">
                  <Star className="w-4 h-4 text-[#F5B041] mb-2" />
                  <p className="text-sm text-[#1A3330] italic">"{t.content}"</p>
                  <p className="text-xs text-[#4A6B68] mt-2">— {t.client_name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
